<template>
  <div class="mobile-subcategories">
    <div class="mobile-subcategories__overlay" @click="closeDialog"></div>

    <div class="mobile-subcategories__box">
      <div class="mobile-subcategories__icon">
        <i
          :class="activeCategory.icon || 'icon-House'"
          :style="`color: ${helperStore.styleData.accentColor}`"
        ></i>
      </div>

      <i class="icon-Cross mobile-subcategories__close" @click="closeDialog"></i>

      <div class="mobile-subcategories__title" :style="{ fontFamily: helperStore.headerFont }">
        <span>{{ activeCategory.title_translations[helperStore.currentLanguage] }}</span>
        <i class="icon-Share-3 share-button" @click="shareCategory"></i>
      </div>

      <div
        class="mobile-subcategories__list"
        ref="subcategoriesListRef"
        @scroll="setScrollPositionDebounced"
      >
        <div
          class="mobile-subcategories__item"
          v-for="(subcategory, index) in activeCategory.subcategories"
          :key="subcategory.id"
          @click="selectSubcategory(index)"
        >
          <span>{{ subcategory.title_translations[helperStore.currentLanguage] }}</span>
          <i class="icon-Arrow-Right"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import debounce from 'lodash/debounce'
import { Category, ShareTypesEnum } from '@/interfaces/guide.interface'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useHelperStore } from '@/pinia/helper.store'

const helperStore = useHelperStore()
const router = useRouter()

const props = defineProps<{
  activeCategory: Category | null
  scrollPosition: number
}>()

const emit = defineEmits<{
  (e: 'update-scroll-position', scrollTop: number): void
  (e: 'close'): void
  (e: 'show-share-overlay', payload: { type: ShareTypesEnum }): void
}>()

const subcategoriesListRef = ref<HTMLDivElement>(null)

onMounted(() => {
  if (props.activeCategory.subcategories.length === 1) {
    closeDialog()
    selectSubcategory(0)
  }

  if (props.scrollPosition && subcategoriesListRef.value) {
    subcategoriesListRef.value.scrollTop = props.scrollPosition
  }
})

const setScrollPositionDebounced = debounce(setScrollPosition, 500)

function setScrollPosition() {
  if (subcategoriesListRef.value) {
    emit('update-scroll-position', subcategoriesListRef.value.scrollTop)
  }
}

function selectSubcategory(subcategoryIndex) {
  const selectedSubcategory = props.activeCategory.subcategories[subcategoryIndex]

  router.push({ params: { subcategoryId: selectedSubcategory.id.toString() }, query: {} })
}

function closeDialog() {
  emit('close')
  emit('update-scroll-position', 0)
  router.push({ query: {} })
}

function shareCategory() {
  emit('show-share-overlay', { type: ShareTypesEnum.CATEGORY })
}
</script>

<style lang="scss" scoped>
.mobile-subcategories {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 100;
    opacity: 0.3;
    background-color: black;
  }
  &__box {
    position: fixed;
    background-color: white;
    min-height: 100px;
    width: 95%;
    border-radius: $border-radius;
    top: 50%;
    transform: translateY(-50%);
    z-index: 101;
    max-width: 500px;
    padding-top: 45px;
  }
  &__title {
    font-size: rem(20);
    margin-top: 20px;
    text-align: center;
    position: relative;

    .share-button {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  &__close {
    position: absolute;
    right: 10px;
    top: 10px;
    font-size: rem(25);
    cursor: pointer;
  }
  &__icon {
    position: absolute;
    left: 50%;
    top: 0;
    width: 90px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translate(-50%, -50%);
    background-color: white;
    border-radius: 50%;
    box-shadow: $info-block-shadow;
    i {
      font-size: rem(50);
    }
  }
  &__list {
    padding: 20px;
    max-height: 300px;
    overflow: auto;
  }
  &__item {
    text-align: left;
    font-size: rem(15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 20px 0;

    &:last-child {
      border-bottom: none;
    }

    i {
      font-size: rem(20);
    }
  }
}
</style>
